import { useCallback } from 'react';
import { useUnmount } from 'react-use';
import { area, curveMonotoneX } from 'd3-shape';
import { scaleLinear, scaleTime } from 'd3-scale';
import { select, Selection } from 'd3-selection';
import { extent } from 'd3-array';

import { useUserPermissionStatus } from 'selectors/user';
import type { SurfDataPoints, SurfDataPoint } from 'types/surf';
import type { SurfHeight } from 'types/units';
import { getGraphYScale, getMaxWaveHeight, scaleWaveHeight } from 'utils/scaleWaveHeight';
import { useForecastDaysAllowed } from 'utils/forecastDaysAllowed';

type Props = {
  spotId: string;
  data: SurfDataPoints;
  parentRef: HTMLDivElement;
  height: number;
  units: SurfHeight;
  smoothingStrength?: number;
  chartIdPrefix?: string;
};

const scaleData = (data: SurfDataPoint[], units: SurfHeight, graphHeight: number) =>
  data.map((dataPoint: SurfDataPoint) => {
    const graphTopPadding = 10;
    const scaledMax = scaleWaveHeight(dataPoint.max, units, graphHeight - graphTopPadding);
    return {
      max: scaledMax,
      timestamp: new Date(dataPoint.timestamp * 1000),
    };
  });

const useSetupCurveGraph = ({
  spotId,
  data,
  parentRef,
  height,
  units,
  smoothingStrength,
  chartIdPrefix,
}: Props) => {
  const idPrefix = chartIdPrefix ? `${chartIdPrefix}-` : '';
  const { hasCoreForecastPermissions } = useUserPermissionStatus();

  const { forecastDaysAllowed } = useForecastDaysAllowed({
    type: 'spot',
    isEntitled: hasCoreForecastPermissions,
  });

  const setupCurveGraph = useCallback(() => {
    const graphDayEl = select(
      `#${idPrefix}day-selector-chart-${spotId} .graph-day`,
    ).node() as HTMLButtonElement;
    const firstDayLeftOffset = graphDayEl?.offsetLeft || 0;
    const graphDayWidth = graphDayEl?.getBoundingClientRect()?.width || 0;
    const chartWidth = graphDayWidth * forecastDaysAllowed;
    const chartHeight = height || parentRef?.offsetHeight;
    const smoothing = smoothingStrength || 4;

    const rawMax = Math.max(...data.map((dataPoint) => dataPoint.max));

    let scaledMax = 0;
    let { min = 0 } = data[0];
    const yAxisScale = getGraphYScale(rawMax, units);

    // setup the swell data based on the selected graph scale
    const swellData = (
      yAxisScale === 'exponent'
        ? scaleData(data, units, chartHeight).map((dataPoint, dayNumber) => {
            // Cuts down on number of data points to make curve smoother
            if (dayNumber % smoothing !== 0) return null;

            if (dataPoint.max > scaledMax) {
              scaledMax = dataPoint.max;
            }

            return {
              timestamp: dataPoint.timestamp,
              max: dataPoint.max,
            };
          })
        : data.map((dataPoint: SurfDataPoint, dayNumber: number) => {
            if (dataPoint.min) {
              if (dataPoint.min < min && dataPoint.min < rawMax) {
                min = dataPoint.min || 0;
              }
            }

            // Cuts down on number of data points to make curve smoother
            if (dayNumber % smoothing !== 0) return null;

            return {
              timestamp: new Date(dataPoint.timestamp * 1000),
              max: dataPoint.max,
            };
          })
    ).filter((dataPoint) => !!dataPoint);

    const rawMaxYDomain = getMaxWaveHeight(rawMax, units);
    const rawMinYDomain = min > 1 ? min - 1 : 0;

    /* gives some cushion to the top of the graph to prevent overflow to headers */
    const paddedMaxYDomain = 0.35 * chartHeight;
    /* gives some cushion to the bottom of the graph to show when it is not quite flat */
    const paddedMinYDomain = chartHeight - 10;

    const xAxis = scaleTime()
      .range([0, chartWidth])
      .domain(extent(swellData, (d) => d?.timestamp) as any);

    const yAxis = scaleLinear()
      .range([paddedMinYDomain, paddedMaxYDomain])
      .domain([rawMinYDomain, rawMaxYDomain]);

    const canvas = select(`#${idPrefix}day-selector-chart-${spotId} .day-selector-chart-content`);
    let svg = select(
      `#${idPrefix}day-selector-chart-${spotId} .day-selector-chart-svg`,
    ) as Selection<SVGSVGElement, unknown, HTMLElement, any>;

    let path = select(
      `#${idPrefix}day-selector-chart-${spotId} .day-selector-chart-svg path`,
    ) as Selection<SVGPathElement, unknown, HTMLElement, any>;

    const swellArea = area()
      .x((d: any) => xAxis(d.timestamp))
      .y0(yAxisScale === 'exponent' ? paddedMinYDomain : yAxis(rawMinYDomain))
      .y1((d: any) => (yAxisScale === 'exponent' ? paddedMinYDomain - d.max : yAxis(d.max)))
      .curve(curveMonotoneX) as any;

    if (!svg.node()) {
      svg = canvas
        .append('svg')
        .attr('class', 'day-selector-chart-svg')
        .attr('data-testid', 'day-selector-chart-svg')
        .style('position', 'relative');

      const defs = svg.append('defs');

      const gradient = defs
        .append('linearGradient')
        .attr('id', 'svgGradient')
        .attr('x1', '0%')
        .attr('x2', '0%')
        .attr('y1', '0%')
        .attr('y2', '100%');

      gradient
        .append('stop')
        .attr('class', 'start')
        .attr('offset', '10%')
        .attr('stop-color', 'rgba(185, 211, 223, 0.8)')
        .attr('stop-opacity', 1);

      gradient
        .append('stop')
        .attr('class', 'end')
        .attr('offset', '110%')
        .attr('stop-color', 'rgba(185, 211, 223, 0)')
        .attr('stop-opacity', 1);

      path = svg
        .append('path')
        .attr('fill', 'url(#svgGradient)')
        .attr('mix-blend-mode', 'multiply');
    }

    canvas.style('width', `${chartWidth}px`);

    svg
      .attr('width', chartWidth)
      .attr('height', chartHeight - 4)
      .style('left', firstDayLeftOffset);

    path
      .attr('d', swellArea(swellData))
      .attr('data-testid', `day-selector-chart-path-${yAxisScale}`);
  }, [
    idPrefix,
    spotId,
    forecastDaysAllowed,
    height,
    parentRef?.offsetHeight,
    smoothingStrength,
    data,
    units,
  ]);

  useUnmount(() => {
    select(`#${idPrefix}day-selector-chart-${spotId} .day-selector-chart-svg`).remove();
  });

  return { setupCurveGraph };
};

export default useSetupCurveGraph;
